import { useQuery } from '@tanstack/react-query';
import { FC } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';

import { CloseButton, StyledDialog } from '@/components/AlertDialogs/Dialog';
import Button from '@/components/Button';
import { FormLayout } from '@/components/Forms';
import FormField from '@/components/Forms/FormField';
import { Modal } from '@/components/Modal';
import PulsingLoader, { LoaderHolder } from '@/components/PulsingLoader';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import useToasts from '@/hooks/useToasts';
import { GSheetImportSpreadsheet } from '@/types/resources';
import { api } from '@/utils/api';
import { handleFormErrors } from '@/utils/helpers';

const Dialog = styled(StyledDialog)`
  width: 520px;
  text-align: left;
`;

const Heading = styled(Text)`
  margin: 0 0 16px;
  text-align: center;
`;

const Footer = styled.div`
  padding: 24px 32px 32px;
  display: flex;
  box-shadow: 0px -1px 8px 0px #2222220f;
  gap: 24px;
  margin: 32px -24px -40px;
`;

const FooterButton = styled(Button)`
  flex: 1;
`;

type FormDataT = {
  spreadsheet_id: string;
  sheet_id: number | undefined;
};

interface Props {
  onClose: VoidFunction;
}

const GSheetImportModal: FC<Props> = ({ onClose }) => {
  const alerts = useAlerts();
  const toasts = useToasts();

  const { data, isLoading } = useQuery(
    ['dq_gsheets_import_options'],
    () =>
      api.get<never, { spreadsheets: GSheetImportSpreadsheet[] }>(
        '/v1/editorial/queue/gsheets-import/options',
      ),
    {
      staleTime: 120_000,
    },
  );

  const spreadsheets = data?.spreadsheets ?? [];

  const {
    control,
    watch,
    setValue,
    setError,
    formState: { isSubmitting },
    handleSubmit,
  } = useForm<FormDataT>({
    defaultValues: {
      spreadsheet_id: '',
      sheet_id: undefined,
    },
  });

  const selectedSpreadsheetId = watch('spreadsheet_id');
  const selectedSheetId = watch('sheet_id');

  const sheets =
    spreadsheets.find((s) => s.id === selectedSpreadsheetId)?.sheets ?? [];

  const onSubmit = handleSubmit(async (formData) => {
    try {
      const response = await api.post<never, { message: string }>(
        '/v1/editorial/queue/gsheets-import',
        {
          spreadsheet_id: formData.spreadsheet_id,
          sheet_ids: [formData.sheet_id],
        },
      );

      onClose();

      toasts.create('success', response.message ?? 'Import started');
    } catch (err) {
      handleFormErrors(err, setError, alerts);
    }
  });

  return (
    <Modal zIndex={50}>
      <Dialog
        initial={{ scale: 0.3 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.3 }}
      >
        <Heading $type="heading2">Run GSheet Video Import</Heading>
        {isLoading ? (
          <LoaderHolder>
            <PulsingLoader />
            <Text $color="gray" $type="captionSemibold">
              Loading...
            </Text>
          </LoaderHolder>
        ) : (
          <FormLayout>
            <FormField<FormDataT, string>
              type="select"
              control={control}
              name="spreadsheet_id"
              label="Spreadsheet"
              placeholder="Select"
              options={spreadsheets.map((s) => ({
                value: s.id,
                label: s.name,
              }))}
              onChangeCallback={() => setValue('sheet_id', undefined)}
            />
            <FormField<FormDataT, number>
              type="select"
              control={control}
              name="sheet_id"
              label="Sheet (tab)"
              placeholder="Select"
              disabled={!selectedSpreadsheetId}
              options={sheets.map((sheet) => ({
                value: sheet.id,
                label: sheet.title,
              }))}
            />
          </FormLayout>
        )}

        <CloseButton onClick={onClose}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13 1L1 13"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M13 13L1 1"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </CloseButton>
        <Footer>
          <FooterButton
            type="button"
            color="primary"
            disabled={
              isSubmitting ||
              !selectedSpreadsheetId ||
              selectedSheetId === undefined
            }
            loading={isSubmitting}
            onClick={onSubmit}
          >
            Run import
          </FooterButton>
          <FooterButton type="button" color="secondary" onClick={onClose}>
            Cancel
          </FooterButton>
        </Footer>
      </Dialog>
    </Modal>
  );
};

export default GSheetImportModal;
